import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard,
  BookOpen,
  Repeat,
  Users,
  Tag,
  FileBarChart,
  Settings,
  LogOut,
  ChevronsUpDown,
  Building2,
  Plus,
  Check,
  UserCog,
  Smartphone,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import NewOrgDialog from "@/components/NewOrgDialog";
import ChurchFlowLogo from "@/components/ChurchFlowLogo";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/ledger", label: "Ledger", icon: BookOpen, testid: "nav-ledger" },
  { to: "/recurring", label: "Recurring", icon: Repeat, testid: "nav-recurring" },
  { to: "/donors", label: "Donors", icon: Users, testid: "nav-donors" },
  { to: "/members", label: "Members", icon: UserCog, testid: "nav-members" },
  { to: "/team", label: "Team", icon: UserCog, testid: "nav-team" },
  { to: "/m", label: "Counter portal", icon: Smartphone, testid: "nav-mobile" },
  { to: "/settings", label: "Settings", icon: Settings, testid: "nav-settings" },
];

export default function Sidebar() {
  const { user, logout, orgs, activeOrgId, switchOrg } = useAuth();
  const nav = useNavigate();
  const [newOrgOpen, setNewOrgOpen] = useState(false);
  const activeOrg = orgs.find((o) => o.id === activeOrgId);

  return (
    <aside
      data-testid="app-sidebar"
      className="w-64 shrink-0 bg-card flex flex-col h-screen sticky top-0 p-3"
    >
      <div className="px-3 pt-4 pb-5">
        <div className="flex items-center gap-2.5">
          <ChurchFlowLogo size={40} className="soft-glow rounded-2xl" />
          <div className="font-serif text-xl leading-none tracking-tight">ChurchFlow</div>
        </div>
      </div>

      <div className="px-1">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              data-testid="org-switcher-button"
              className="w-full flex items-center justify-between gap-2 px-3 py-2.5 rounded-2xl bg-secondary hover:bg-secondary/70 transition-colors text-left"
            >
              <div className="flex items-center gap-2 min-w-0">
                <Building2 className="w-4 h-4 text-muted-foreground shrink-0" />
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                    Organization
                  </div>
                  <div className="text-sm font-medium truncate">
                    {activeOrg?.name || "Select organization"}
                  </div>
                </div>
              </div>
              <ChevronsUpDown className="w-4 h-4 text-muted-foreground shrink-0" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-60 rounded-xl">
            <DropdownMenuLabel className="font-serif text-base">Your Organizations</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {orgs.map((o) => (
              <DropdownMenuItem
                key={o.id}
                data-testid={`org-option-${o.id}`}
                onSelect={() => switchOrg(o.id)}
                className="flex items-center justify-between rounded-lg"
              >
                <span className="truncate">{o.name}</span>
                {o.id === activeOrgId && <Check className="w-4 h-4 text-primary" />}
              </DropdownMenuItem>
            ))}
            <DropdownMenuSeparator />
            <DropdownMenuItem
              data-testid="new-org-menu-item"
              onSelect={() => setNewOrgOpen(true)}
              className="rounded-lg"
            >
              <Plus className="w-4 h-4 mr-2" /> New organization
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <nav className="mt-6 flex-1 px-1 space-y-1">
        {NAV.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            data-testid={item.testid}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 ${
                isActive
                  ? "bg-primary text-primary-foreground shadow-sm soft-glow"
                  : "text-foreground/75 hover:bg-secondary hover:text-foreground"
              }`
            }
          >
            <item.icon className="w-4 h-4" strokeWidth={2.2} />
            <span>{item.label}</span>
          </NavLink>
        ))}
      </nav>

      <div className="p-1 mt-3">
        <div className="rounded-2xl bg-secondary/60 p-3">
          <div className="px-1 pb-1">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
              Signed in as
            </div>
            <div className="text-sm font-medium truncate" data-testid="signed-in-email">
              {user?.email}
            </div>
          </div>
          <Button
            data-testid="logout-button"
            variant="ghost"
            className="w-full justify-start mt-1 rounded-xl text-foreground/80 hover:text-destructive hover:bg-destructive/10"
            onClick={async () => {
              await logout();
              nav("/login");
            }}
          >
            <LogOut className="w-4 h-4 mr-2" /> Sign out
          </Button>
        </div>
      </div>

      <NewOrgDialog open={newOrgOpen} onOpenChange={setNewOrgOpen} />
    </aside>
  );
}
